import { useMemo } from "react";

export default function useAuth() {
  // Stable when the stored value is unchanged — avoids new object refs every render from JSON.parse,
  // which breaks useCallback/useEffect dependency equality across the app.
  const userJson =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;

  const user = useMemo(() => {
    if (!userJson || userJson === "undefined" || userJson === "null") {
      return null;
    }
    try {
      return JSON.parse(userJson);
    } catch (err) {
      console.error("Failed to parse user from localStorage:", err);
      return null;
    }
  }, [userJson]);

  // Determine role - match backend logic in permissions.py
  let role = "Guest";
  if (user) {
    if (user.is_superuser) {
      role = "Admin";
    } else if (user.role) {
      role = user.role;
    } else if (user.groups && Array.isArray(user.groups) && user.groups.length > 0) {
      // Get first group name (matching backend logic)
      const groupName = user.groups[0]?.name || user.groups[0];
      if (groupName) {
        role = groupName;
      }
    }
  }

  // Normalize role names to match backend expectations
  const normalizedRole = normalizeRole(role);
  
  // ✅ Check if user is read-only (boolean field, not group)
  const isReadOnly = user?.is_read_only === true;
  const canViewAdditionalNotes = user?.can_view_additional_notes !== false;

  const can = {
    // Users / Roles
    // ✅ All permissions respect read-only
    createUser: !isReadOnly && ["Admin", "Sales Director"].includes(normalizedRole),
    editAnyUser: !isReadOnly && ["Admin", "Sales Director"].includes(normalizedRole),
    editUsersInOwnTerritory: !isReadOnly && normalizedRole === "Manager",
    editSelf: !isReadOnly && ["Admin", "Sales Director", "Manager", "Sales Rep"].includes(normalizedRole),

    // Territories
    createTerritory: !isReadOnly && ["Admin", "Sales Director", "Manager"].includes(normalizedRole),
    editAnyTerritory: !isReadOnly && ["Admin", "Sales Director"].includes(normalizedRole),
    editOwnTerritory: !isReadOnly && normalizedRole === "Manager",

    // Leads
    createLead: !isReadOnly && ["Admin", "Sales Director", "Manager", "Sales Rep"].includes(normalizedRole),
    editAnyLead: !isReadOnly && ["Admin", "Sales Director"].includes(normalizedRole),
    editLeadsInOwnTerritory: !isReadOnly && normalizedRole === "Manager",
    editOwnLeads: !isReadOnly && normalizedRole === "Sales Rep",

    // Deletes (UI should hide destructive actions for non-Admin)
    // ✅ Sales Directors cannot delete - this remains Admin-only
    canDelete: !isReadOnly && normalizedRole === "Admin",

    // Settings (Admin & Sales Director - full access)
    // ✅ Updated to include Sales Director
    canAccessSettings: !isReadOnly && ["Admin", "Sales Director"].includes(normalizedRole),
  };

  return { 
    role: normalizedRole, 
    can,
    isReadOnly,  // ✅ Export this
    canViewAdditionalNotes,
    user,
  };
}

// Normalize role names to match backend expectations
// Handles variations like "Territory Manager" -> "Manager", "sales rep" -> "Sales Rep"
function normalizeRole(role) {
  if (!role) return "Guest";
  
  const roleLower = role.toLowerCase().trim();
  
  // Map variations to standard role names
  if (roleLower === "admin" || roleLower === "administrator") {
    return "Admin";
  }
  if (roleLower.includes("sales director") || roleLower === "director") {
    return "Sales Director";
  }
  if (roleLower.includes("manager") || roleLower === "territory manager" || roleLower === "regional manager") {
    return "Manager";
  }
  if (roleLower.includes("sales rep") || roleLower === "rep" || roleLower === "sales representative") {
    return "Sales Rep";
  }
  
  // Return capitalized version if no match
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}
